import { defaultShouldDehydrateQuery, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { ReactNode } from "react";

let clientQueryClientSingleton: QueryClient | undefined;

export const createQueryClient = () => {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 30 * 1000, // 30 seconds
				gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime)
				retry: 1,
				retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
				refetchOnWindowFocus: true, // refetch on window focus
				refetchOnMount: true, // refetch on mount
				refetchOnReconnect: true, // refetch on reconnect
			},
			mutations: {
				retry: 1,
				retryDelay: 1000,
			},
			dehydrate: {
				shouldDehydrateQuery: (query) => defaultShouldDehydrateQuery(query) || query.state.status === "pending",
			},
		},
	});
};

export const queryClientSingleton = () => {
	if (typeof window === "undefined") {
		// Server-side: always create a new client
		return createQueryClient();
	}

	// Client-side: use singleton pattern
	if (!clientQueryClientSingleton) {
		clientQueryClientSingleton = createQueryClient();
	}

	return clientQueryClientSingleton;
};

export const ReactProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = queryClientSingleton();
	const isDev = process.env.NODE_ENV === "development";

	return (
		<QueryClientProvider client={queryClient}>
			{children}
			{isDev && (
				<ReactQueryDevtools
					initialIsOpen={false}
					buttonPosition="bottom-left"
				/>
			)}
		</QueryClientProvider>
	);
};
